<template>
	<div class="comparison_chart">
		<div class="comparison_controls">
			<!-- Leyenda de años: cada chip pinta su dot con el MISMO color que su
				línea. Click resalta ese año (su línea pasa al teal del año
				actual; el resto quedan grises) para identificar qué línea es
				cuál. La × (visible al hover) lo quita; "+ Añadir año" agrega el
				siguiente año disponible más reciente. -->
			<div class="years_legend">
				<div v-for="year in orderedYears" :key="year" class="year_chip">
					<button type="button" class="year_label"
						:class="{ 'is-highlighted': year === activeHighlight }"
						@click="highlightYear(year)">
						<span class="year_dot" :style="{ backgroundColor: colorForYear(year) }"></span>
						{{ year }}
					</button>
					<button v-if="orderedYears.length > 1" type="button"
						class="year_remove" title="Quitar año"
						@click="$emit('remove-year', year)">×</button>
				</div>
				<button v-if="nextAvailableYear" type="button" class="add_year_btn"
					@click="$emit('add-year', nextAvailableYear)">
					+ Añadir año
				</button>
			</div>

			<!-- Subcategorías (tiendas/marcas) — selección única. Vacío en modo
				cuentas (sin subcategorías). -->
			<div v-if="subItems.length" class="sub_items">
				<button v-for="opt in subItems" :key="opt" type="button"
					class="filter_btn"
					:class="{ 'is-active': opt === selectedItem }"
					@click="$emit('select-item', opt)">
					{{ opt }}
				</button>
			</div>

			<!-- Acciones a la derecha de la leyenda de años (opt-in). Lo usa el
				detalle de contraparte para el toggle de métrica (monto / % de
				ventas); las tablas no pasan el slot. -->
			<div v-if="$slots['legend-actions']" class="legend_actions">
				<slot name="legend-actions" />
			</div>
		</div>

		<div class="chart_body">
			<div v-if="isLoading" class="state_msg">Cargando comparación...</div>
			<div v-else-if="error" class="state_msg has-error">{{ error }}</div>
			<div v-else-if="!series.length" class="state_msg">Sin datos para comparar.</div>
			<EChart v-else-if="chartReady" :option="chartOption"
				@chart-mouseover="onSeriesHover" @chart-mouseout="onSeriesOut" />
		</div>
	</div>
</template>

<script>
import moment from "moment";
import EChart from "@/components/charts/EChart.vue";
import { parseToCompactMoney, parseToMoney } from "@/utils/currency";
import tooltipFocusMixin from "./tooltipFocusMixin";

// Etiquetas de mes en español, indexadas por número de mes (1..12). El backend
// manda `months` como enteros; el eje X los necesita como labels legibles.
const MONTH_ABBR = [
	"Ene", "Feb", "Mar", "Abr", "May", "Jun",
	"Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

// Anchors de color resueltos en mounted (canvas no resuelve var(--...)).
// El año más reciente = teal accent (la línea "viva"); los previos degradan
// del gris medio al claro por antigüedad.
const ACCENT_ANCHOR = "--report-chart-accent";
const PREV_ANCHORS = [
	"--report-chart-prev-1",
	"--report-chart-prev-2",
	"--report-chart-prev-3",
];
const TEXT_ANCHOR = "--report-text";
const TEXT_MUTED_ANCHOR = "--report-text-muted";
const BORDER_ANCHOR = "--report-border";
const TOOLTIP_BG_ANCHOR = "--report-tooltip-bg";
const TOOLTIP_TEXT_ANCHOR = "--report-tooltip-text";

export default {
	name: "SalesComparisonChart",

	components: { EChart },

	mixins: [tooltipFocusMixin],

	props: {
		// Serie por año, alineada a `months`. `values[i]` puede ser null
		// (mes futuro del año en curso) → la línea se corta ahí.
		series: { type: Array, default: () => [] },
		// Números de mes (1..12) del eje X.
		months: { type: Array, default: () => [] },
		isLoading: { type: Boolean, default: false },
		error: { type: String, default: "" },
		// Subcategorías de la categoría activa (vacío en cuentas/clientes).
		subItems: { type: Array, default: () => [] },
		selectedItem: { type: String, default: null },
		// Años seleccionados y años con datos disponibles (para "+ Añadir año").
		years: { type: Array, default: () => [] },
		availableYears: { type: Array, default: () => [] },
		// Granularidad del eje X. El backend siempre devuelve 12 meses; con
		// "quarter" agregamos esos meses en 4 buckets (Q1..Q4) acá, en display,
		// sin refetch. Cualquier otro valor (month/ytd/year) cae a mensual.
		period: {
			type: String,
			default: "month",
			validator: (v) => ["month", "quarter", "ytd", "year"].includes(v),
		},
		// Formato de los valores (eje Y + tooltip). "money" (default, las tablas)
		// o "percent" (comparación de % de ventas en el detalle de contraparte).
		valueFormat: {
			type: String,
			default: "money",
			validator: (v) => ["money", "percent"].includes(v),
		},
	},

	data() {
		return {
			chartReady: false,
			// Año resaltado (su línea va en teal). null → default = el más
			// reciente. Es estado de vista local: no se comparte ni persiste.
			highlightedYear: null,
			accentColor: "",
			prevColors: [],
			textColor: "",
			textMutedColor: "",
			borderColor: "",
			tooltipBg: "",
			tooltipText: "",
		};
	},

	computed: {
		// Años de mayor a menor — fija el orden de render de la leyenda y el
		// orden del ramp de grises de los años no resaltados.
		orderedYears() {
			return [...this.years].sort((a, b) => b - a);
		},

		// Año actualmente resaltado (línea en teal). Si el usuario no eligió
		// uno —o el que había eligió ya no está en la lista—, cae al más
		// reciente, que es el "año actual" por defecto.
		activeHighlight() {
			if (this.highlightedYear != null && this.years.includes(this.highlightedYear)) {
				return this.highlightedYear;
			}
			return this.orderedYears.length ? this.orderedYears[0] : null;
		},

		// Siguiente año disponible (más reciente) que aún no está seleccionado.
		nextAvailableYear() {
			return (this.availableYears || []).find(
				(y) => !this.years.includes(y)
			) || null;
		},

		monthLabels() {
			return (this.months || []).map((m) => MONTH_ABBR[m - 1] || String(m));
		},

		isQuarterly() {
			return this.period === "quarter";
		},

		// Buckets de quarter presentes según `months`, en orden ascendente. Cada
		// bucket guarda los índices de los meses que caen en ese trimestre, para
		// reusarlos al agregar los valores de cada serie. Genérico sobre `months`
		// (no asume los 12 completos) por robustez.
		quarterBuckets() {
			const buckets = [];
			(this.months || []).forEach((m, idx) => {
				const q = Math.ceil(m / 3);
				let bucket = buckets.find((b) => b.q === q);
				if (!bucket) {
					bucket = { q, idxs: [] };
					buckets.push(bucket);
				}
				bucket.idxs.push(idx);
			});
			return buckets.sort((a, b) => a.q - b.q);
		},

		// Labels del eje X según granularidad: meses (Ene..Dic) o quarters
		// (Q1..Q4).
		axisLabels() {
			if (!this.isQuarterly) return this.monthLabels;
			return this.quarterBuckets.map((b) => `Q${b.q}`);
		},

		// Año en curso: si el valor del mes actual todavía es 0 (mes recién
		// iniciado, sin ventas devengadas aún), lo tratamos como null para que la
		// línea corte en el mes anterior en lugar de desplomarse a cero en el mes
		// actual. Sólo afecta al año calendario corriente y sólo cuando el valor
		// es exactamente 0 (un mes con ventas reales = 0 no aplica acá: en la
		// práctica el backend manda 0 mientras no haya devengo del mes).
		normalizedSeries() {
			const currentYear = moment().year();
			const currentMonth = moment().month() + 1; // moment month es 0-based
			const idx = (this.months || []).indexOf(currentMonth);
			if (idx === -1) return this.series;
			return this.series.map((serie) => {
				if (serie.year !== currentYear || serie.values[idx] !== 0) return serie;
				const values = serie.values.slice();
				values[idx] = null;
				return { ...serie, values };
			});
		},

		// Series listas para graficar. En mensual pasan tal cual; en trimestral
		// sumamos los meses de cada quarter. Un quarter es `null` (corta la
		// línea) sólo si TODOS sus meses son null — el año en curso, con meses
		// futuros en null, dibuja así su trimestre parcial y corta después.
		displaySeries() {
			if (!this.isQuarterly) return this.normalizedSeries;
			return this.normalizedSeries.map((serie) => ({
				year: serie.year,
				values: this.quarterBuckets.map((bucket) => {
					const present = bucket.idxs
						.map((i) => serie.values[i])
						.filter((v) => v != null);
					return present.length
						? present.reduce((acc, v) => acc + v, 0)
						: null;
				}),
			}));
		},

		chartOption() {
			return {
				grid: { top: 16, right: 24, bottom: 32, left: 56, containLabel: false },
				legend: { show: false },
				tooltip: {
					trigger: "axis",
					confine: true,
					backgroundColor: this.tooltipBg,
					borderColor: this.tooltipBg,
					textStyle: { color: this.tooltipText, fontSize: 12 },
					formatter: (params) => {
						if (!Array.isArray(params) || params.length === 0) return "";
						const rows = params
							.filter((p) => p.value != null)
							.map((p) => `
								<div style="display:flex;justify-content:space-between;gap:12px;align-items:baseline;${this.focusRowStyle(p.seriesName)}">
									<span>${p.marker}${p.seriesName}</span>
									<span style="flex-shrink:0;">${this.formatValue(p.value)}</span>
								</div>
							`).join("");
						return `
							<div style="font-weight:600;margin-bottom:6px;">${params[0].axisValueLabel}</div>
							${rows}
						`;
					},
				},
				xAxis: {
					type: "category",
					data: this.axisLabels,
					boundaryGap: false,
					axisLine: { lineStyle: { color: this.borderColor } },
					axisTick: { show: false },
					axisLabel: { color: this.textMutedColor, fontSize: 11, margin: 12 },
				},
				yAxis: {
					type: "value",
					axisLine: { show: false },
					axisTick: { show: false },
					splitLine: { lineStyle: { color: this.borderColor, type: "dashed" } },
					axisLabel: {
						color: this.textMutedColor,
						fontSize: 11,
						formatter: (val) => this.formatValue(val, true),
					},
				},
				series: this.displaySeries.map((serie) => {
					const color = this.colorForYear(serie.year);
					return {
						name: String(serie.year),
						type: "line",
						smooth: true,
						showSymbol: false,
						// El año en curso trae null en los meses futuros — cortamos
						// la línea ahí en lugar de caer a cero hacia diciembre.
						connectNulls: false,
						lineStyle: { width: 2, color },
						itemStyle: { color },
						emphasis: { focus: "series" },
						data: serie.values,
					};
				}),
			};
		},
	},

	mounted() {
		this.resolveCssColors();
		this.chartReady = Boolean(this.accentColor);
	},

	methods: {
		// Formatea un valor del eje/tooltip según `valueFormat`. En "percent" los
		// porcentajes van como enteros (regla del reporte). `compact` lo usa el
		// eje Y (montos abreviados); el tooltip pasa el monto completo.
		formatValue(value, compact = false) {
			if (value == null) return "—";
			if (this.valueFormat === "percent") return `${Math.round(value)}%`;
			return compact ? parseToCompactMoney(value) : parseToMoney(value);
		},

		colorForYear(year) {
			// El año resaltado va en teal; el resto degradan en grises por
			// recencia (entre los no resaltados) para que sigan distinguibles.
			if (year === this.activeHighlight) return this.accentColor;
			const others = this.orderedYears.filter((y) => y !== this.activeHighlight);
			const idx = others.indexOf(year);
			return this.prevColors[Math.min(idx, this.prevColors.length - 1)]
				|| this.accentColor;
		},

		highlightYear(year) {
			this.highlightedYear = year;
		},

		resolveCssColors() {
			if (typeof window === "undefined") return;
			// De un elemento del subárbol del tab (no :root): la paleta vive en el
			// `[data-theme]` del contenedor y se hereda por cascada — así la línea
			// del año en curso toma el accent del tab (ámbar en Gastos). En mounted
			// el root es un comment (v-if="chartReady"), así que caemos al
			// parentElement y, en último caso, a documentElement.
			const host = this.$el && this.$el.nodeType === 1
				? this.$el
				: (this.$el && this.$el.parentElement) || document.documentElement;
			const root = getComputedStyle(host);
			this.accentColor = root.getPropertyValue(ACCENT_ANCHOR).trim();
			this.prevColors = PREV_ANCHORS.map(
				(anchor) => root.getPropertyValue(anchor).trim()
			);
			this.textColor = root.getPropertyValue(TEXT_ANCHOR).trim();
			this.textMutedColor = root.getPropertyValue(TEXT_MUTED_ANCHOR).trim();
			this.borderColor = root.getPropertyValue(BORDER_ANCHOR).trim();
			this.tooltipBg = root.getPropertyValue(TOOLTIP_BG_ANCHOR).trim();
			this.tooltipText = root.getPropertyValue(TOOLTIP_TEXT_ANCHOR).trim();
		},
	},
};
</script>

<style scoped lang="scss">
.comparison_chart {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.comparison_controls {
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px;
}

.years_legend {
	display: flex;
	align-items: center;
	gap: 16px;
}

// Slot de acciones a la derecha de la leyenda (toggle de métrica del detalle de
// contraparte). margin-left:auto lo empuja al extremo aunque haya sub_items.
.legend_actions {
	display: flex;
	align-items: center;
	gap: 6px;
	margin-left: auto;
}

.year_chip {
	display: inline-flex;
	align-items: center;
	gap: 2px;

	// La × de quitar sólo aparece al hover del chip, para no saturar la
	// leyenda — la acción principal del chip es resaltar, no quitar.
	&:hover .year_remove {
		opacity: 1;
	}
}

.year_label {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border: none;
	background: transparent;
	padding: 2px 4px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted);
	cursor: pointer;

	&:hover { color: var(--report-text); }

	// Año resaltado: además del dot teal, reforzamos con el color de texto
	// pleno para que el chip activo "salte" en la leyenda.
	&.is-highlighted {
		color: var(--report-text);
		font-weight: 600;
	}
}

.year_dot {
	width: 10px;
	height: 10px;
	border-radius: 50%;
	display: inline-block;
}

.year_remove {
	border: none;
	background: transparent;
	padding: 0 2px;
	font-size: 16px;
	line-height: 1;
	color: var(--report-text-muted);
	cursor: pointer;
	opacity: 0;
	transition: opacity 0.15s ease;

	&:hover { color: var(--report-negative); }
	&:focus-visible { opacity: 1; }
}

.add_year_btn {
	border: none;
	background: transparent;
	padding: 2px 4px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	cursor: pointer;

	&:hover { color: var(--report-primary); }
}

.sub_items {
	display: flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
}

.filter_btn {
	padding: 6px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.2s;

	&:hover { background: var(--report-filter-wash, var(--report-surface)); }

	&.is-active {
		background: var(--report-filter-accent, var(--report-primary));
		color: var(--report-bg);

		&:hover { background: var(--report-filter-accent-hover, var(--report-primary-hover)); }
	}
}

.chart_body {
	height: 340px;
}

:deep(.echart) {
	width: 100%;
	height: 100%;
}

.state_msg {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	color: var(--report-text-muted);
	font-size: 14px;

	&.has-error { color: var(--report-negative); }
}
</style>
